import { Reveal, SectionHead } from "./Reveal";
import { Briefcase, Store, ShoppingCart, UtensilsCrossed, Scissors, Building2, Car, PenTool, ArrowUpRight } from "lucide-react";
import { waLink } from "@/lib/site";

const SERVICES = [
  [Briefcase, "Business Websites", "Professional websites for companies, local businesses and service providers.", "/assets/service-business.webp"],
  [Store, "Small Business Websites", "Affordable professional websites for shops, startups and growing businesses.", "/assets/service-small-business.webp"],
  [ShoppingCart, "E-Commerce Websites", "Modern online stores designed to showcase products and generate enquiries and sales.", "/assets/service-ecommerce.webp"],
  [UtensilsCrossed, "Restaurant & Hotel Websites", "Menus, galleries, location, contact, WhatsApp and booking/enquiry features.", "/assets/service-restaurant.webp"],
  [Scissors, "Salon & Clinic Websites", "Professional service presentation, appointment/enquiry features and customer contact options.", "/assets/service-salon.webp"],
  [Building2, "Real Estate Websites", "Property listings, galleries, enquiry forms and lead-generation features.", "/assets/service-real-estate.webp"],
  [Car, "Used Car Dealer Websites", "Vehicle listings, photo galleries, enquiry buttons, test-drive requests and WhatsApp leads.", "/assets/service-used-cars.webp"],
  [PenTool, "Custom Websites", "Custom websites based on specific business requirements.", "/assets/service-custom.webp"],
];

export const Services = () => (
  <section id="services" data-testid="services-section" className="relative bg-obsidian py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <SectionHead
        eyebrow="What we build"
        title="Websites designed around your business"
        sub="Not around a generic template. Every build is structured for your customers, your services and your goals."
      />
      <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        {SERVICES.map(([Icon, title, desc, img], i) => (
          <Reveal key={title} delay={(i % 4) * 0.08}>
            <a
              href={waLink(`Hi Falkeen Technologies, I'm interested in a ${title.replace(/ Websites?$/, "")} website for my business.`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`service-card-${i + 1}`}
              className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/5 bg-charcoal p-5 transition-[border-color,box-shadow,transform] duration-500 hover:-translate-y-1.5 hover:border-accent/30 hover:shadow-[0_0_40px_rgba(0,229,255,0.12)]"
            >
              <span className="pointer-events-none absolute -right-2 -top-4 z-10 font-display text-7xl font-black text-white/[0.06] transition-colors duration-500 group-hover:text-accent/10" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="relative aspect-video overflow-hidden rounded-xl">
                <img
                  src={img}
                  alt={title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent" />
              </div>
              <div className="mt-5 flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-white/5 transition-colors duration-500 group-hover:border-accent/40 group-hover:bg-accent/10">
                <Icon className="h-5 w-5 text-accent" strokeWidth={1.5} />
              </div>
              <h3 className="mt-6 font-display text-base font-extrabold uppercase leading-snug tracking-wide text-white">
                {title}
              </h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-zinc-400">{desc}</p>
              <span className="mt-6 flex items-center gap-1.5 font-display text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 transition-colors duration-300 group-hover:text-accent">
                Enquire Now
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </a>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
